import { INCIDENT_CATEGORIES } from "./categories.js";
import { deidentify } from "./deidentify.js";
const MAX_CHARS = 32000;
const TASK_TEXT = "Identify the root cause and propose a specific fix. Cite the exact function and lines at " +
    "fault and give the corrected logic. If the report is too vague to act on, say so and state " +
    "precisely what additional information would resolve it — do not guess at a fix.";
function categoryLabel(categoryId) {
    const match = INCIDENT_CATEGORIES.find((c) => c.id === categoryId);
    return match ? match.label : categoryId;
}
function appLabel(appId) {
    return appId
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map((word) => word[0].toUpperCase() + word.slice(1))
        .join(" ");
}
function daysBetween(earlier, later) {
    const ms = new Date(later).getTime() - new Date(earlier).getTime();
    return Math.max(0, Math.round(ms / (24 * 60 * 60 * 1000)));
}
function buildWhatTheUserSaid(incident) {
    const lines = [];
    if (incident.type === "rating" && incident.rating) {
        lines.push(`- Rating: ${incident.rating}`);
    }
    if (incident.category) {
        lines.push(`- Category: ${categoryLabel(incident.category)}`);
    }
    lines.push(`- Their words: "${incident.body}"`);
    const otherReporters = incident.occurrences - 1;
    const occurrenceNote = otherReporters > 0
        ? `  (${otherReporters} other user${otherReporters === 1 ? "" : "s"} reported the same thing — see below)`
        : "";
    lines.push(`- Reported: ${incident.createdAt}${occurrenceNote}`);
    return { heading: "## What the user said", body: lines.join("\n") };
}
function buildWhere(incident, surface) {
    const lines = [
        `- Surface: ${surface.label}  (${incident.context.surfaceId})`,
        `- Route: ${incident.context.route}`,
        `- Build: ${incident.appVersion}`,
    ];
    return { heading: "## Where", body: lines.join("\n") };
}
function buildEnvironment(incident) {
    const ctx = incident.context;
    const line = `- ${ctx.browser} on ${ctx.os}, ${ctx.deviceClass}, ${ctx.viewport}, ${ctx.locale}, ${ctx.timezone}, ${ctx.prefersColorScheme}`;
    return { heading: "## Environment", body: line };
}
function buildStateAtTheTime(incident, surface) {
    const payload = incident.context.payload;
    if (!payload || Object.keys(payload).length === 0)
        return null;
    const body = surface.renderPayload
        ? surface.renderPayload(payload)
        : "```json\n" + JSON.stringify(payload, null, 2) + "\n```";
    return { heading: "## State at the time", body };
}
function buildRelatedReports(incident, related) {
    if (!related || related.length === 0)
        return null;
    const sorted = [...related].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const top = sorted.slice(0, 5);
    const remaining = sorted.length - top.length;
    const lines = top.map((r) => {
        const days = daysBetween(r.createdAt, incident.createdAt);
        const ago = days === 0 ? "today" : `${days} day${days === 1 ? "" : "s"} ago`;
        const snippet = r.body.length > 120 ? `${r.body.slice(0, 120)}…` : r.body;
        return `- ${r.ref} (${ago}): "${snippet}"`;
    });
    if (remaining > 0) {
        lines.push(`- [truncated — ${remaining} more]`);
    }
    return { heading: "## Related reports", body: lines.join("\n") };
}
function buildWhereToLookFirst(surface) {
    if (surface.likelyFiles.length === 0)
        return null;
    return {
        heading: "## Where to look first",
        body: surface.likelyFiles.map((f) => `- ${f}`).join("\n"),
    };
}
function renderSection(section) {
    return `${section.heading}\n${section.body}`;
}
function truncateToFit(sections, headerAndTask) {
    const overhead = headerAndTask.length + sections.length * 2; // join separators
    let budget = MAX_CHARS - overhead;
    if (budget < 0)
        budget = 0;
    const total = sections.reduce((sum, s) => sum + renderSection(s).length, 0);
    if (total <= budget)
        return sections;
    // Truncate the single longest section until the whole prompt fits.
    const longestIndex = sections.reduce((best, s, i) => (s.body.length > sections[best].body.length ? i : best), 0);
    const excess = total - budget;
    const target = sections[longestIndex];
    const truncatedLength = Math.max(0, target.body.length - excess - 40);
    const removedChars = target.body.length - truncatedLength;
    const truncatedSections = [...sections];
    truncatedSections[longestIndex] = {
        ...target,
        body: `${target.body.slice(0, truncatedLength)}\n[truncated — ${removedChars} more]`,
    };
    return truncatedSections;
}
export function buildImprovementPrompt(incident, surface, opts) {
    const target = opts?.deidentify ? deidentify(incident) : incident;
    const related = opts?.deidentify
        ? opts.includeRelated?.map((r) => deidentify(r))
        : opts?.includeRelated;
    const typeLabel = {
        bug: "A user reported a bug.",
        feature: "A user requested a feature.",
        contact: "A user sent a message.",
        rating: "A user gave this a thumbs-down.",
        nps: "A user responded to a satisfaction prompt.",
    };
    const header = `You are improving ${appLabel(target.appId)}. ${surface.promptFraming}\n` +
        `${typeLabel[target.type]} Below is everything captured at the moment they did.`;
    const taskSection = renderSection({ heading: "## Task", body: TASK_TEXT });
    const sections = [
        buildWhatTheUserSaid(target),
        buildWhere(target, surface),
        buildEnvironment(target),
        buildStateAtTheTime(target, surface),
        buildRelatedReports(target, related),
        buildWhereToLookFirst(surface),
    ].filter((s) => s !== null);
    const fitted = truncateToFit(sections, `${header}\n\n${taskSection}`);
    return [header, ...fitted.map(renderSection), taskSection].join("\n\n");
}
